import Swal, { type SweetAlertIcon } from 'sweetalert2';
// import '@sweetalert2/theme-bulma/bulma.css';

/**
 * SVG de remixicon (ri-shield-keyhole-fill) para usar como `iconoPersonalizado`
 * en alertas de validación de seguridad (ej. contraseña reutilizada, cambio obligatorio).
 */
export const ICONO_VALIDACION_SEGURIDAD = '<svg viewBox="0 0 24 24" width="30" height="30" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M12 1L20.2169 2.82598C20.6745 2.92766 21 3.33347 21 3.80217V13.7889C21 15.795 19.9974 17.6684 18.3282 18.7812L12 23L5.6718 18.7812C4.00261 17.6684 3 15.795 3 13.7889V3.80217C3 3.33347 3.32553 2.92766 3.78307 2.82598L12 1ZM12 7C10.8954 7 10 7.89543 10 9C10 9.74025 10.4022 10.3866 10.9999 10.7324L11 15H13L13.0011 10.7318C13.5983 10.3858 14 9.73984 14 9C14 7.89543 13.1046 7 12 7Z"/></svg>';
export const ICONO_VALIDACION_SEGURIDAD_V2 = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="30" height="30" fill="white"><path d="M6 8V7C6 3.68629 8.68629 1 12 1C15.3137 1 18 3.68629 18 7V8H20C20.5523 8 21 8.44772 21 9V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V9C3 8.44772 3.44772 8 4 8H6ZM19 10H5V20H19V10ZM11 15.7324C10.4022 15.3866 10 14.7403 10 14C10 12.8954 10.8954 12 12 12C13.1046 12 14 12.8954 14 14C14 14.7403 13.5978 15.3866 13 15.7324V18H11V15.7324ZM8 8H16V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V8Z"></path></svg>';

export interface CustomAlertParams {
    /** Mensaje de texto */
    mensaje?: string | null;
    /** Tiempo (segundos) en el que el mensaje será visible antes de cerrarse */
    tiempo?: number;
    /** Si `true` el botón cancelar será visible (predeterminado: `false`) */
    botonCancelar?: boolean | null;
    /** Si `true` el loader será visible (predeterminado `false`) */
    loading?: boolean | null;
    /** Título de la alerta */
    titulo?: string | null;
    /** Texto del botón aceptar */
    textoBotonAceptar?: string | null;
    /** Color bg del botón aceptar */
    colorBotonAceptar?: string | null;
    /** Texto del botón cancelar */
    textoBotonCancelar?: string | null;
    /** Color bg del botón cancelar */
    colorBotonCancelar?: string | null;
    /** Icono de la alerta (predeterminado: `success`) */
    icon?: SweetAlertIcon;
    /** Tamaño del icono en píxeles (predeterminado: toast = 32px, modal = 45px) */
    iconSize?: number;
    /** Función callback */
    callback?: ((isConfirmed: boolean) => void) | null;
    /** Si `true` cierra el modal al hacer clic fuera del modal */
    cerrarClicFuera?: boolean;
    /** Si `true` cierra el modal al pulsar la tecla escape */
    cerrarEsc?: boolean;
    /** Si `true` muestra un toast en lugar de un modal */
    toast?: boolean;
    /** Theme personalizado para el toast o modal */
    theme?: 'light' | 'dark' | 'colored';
    posicionToast?: 'top' | 'top-start' | 'top-end' | 'center' | 'center-start' | 'center-end' | 'bottom' | 'bottom-start' | 'bottom-end';
    /** Mensaje de tiempo para mostrar en la alerta */
    mensajeTiempoSWL?: string;
    /** HTML de un ícono personalizado (ej. `ICONO_VALIDACION_SEGURIDAD`). Si se define, sustituye el ícono nativo de SweetAlert2 por una insignia circular con este ícono, compuesta junto al título. */
    iconoPersonalizado?: string;
}

/**
 * @description Muestra una alerta personalizada usando SweetAlert2.
 * Si loading = `true` el botón aceptar no será visible, en su lugar se mostrará el loading.
 * Si toast = `true` muestra un toast en lugar de un modal.
 */
export function customAlert(params: CustomAlertParams): void {
    const {
        mensaje = '',
        botonCancelar = false,
        loading = false,
        icon = 'success',
        iconSize = params.toast ? 32 : 45,
        callback = null,
        titulo = null,
        textoBotonAceptar = 'Aceptar',
        colorBotonAceptar = '#0050dd',
        textoBotonCancelar = 'Cancelar',
        colorBotonCancelar = '#ef3340',
        cerrarClicFuera = true,
        cerrarEsc = true,
        tiempo,
        toast = false,
        theme = 'light',
        posicionToast = 'top-end',
        mensajeTiempoSWL = 'Este mensaje se ocultará en',
        iconoPersonalizado
    } = params;

    const clic = cerrarClicFuera === true;
    const esc = cerrarEsc === true;

    // Configuración para toast
    if (toast) {
        const Toast = Swal.mixin({
            toast: true,
            position: posicionToast,
            showConfirmButton: false,
            timer: tiempo && tiempo > 0 ? tiempo * 1000 : 4000,
            timerProgressBar: true,
            didOpen: (toastElement) => {
                toastElement.addEventListener('mouseenter', Swal.stopTimer);
                toastElement.addEventListener('mouseleave', Swal.resumeTimer);
                
                // Aplicar tamaño del icono si se especificó
                if (iconSize) {
                    // const iconElement = toastElement.querySelector('.swal2-icon');
                    // if (iconElement instanceof HTMLElement) {
                    //     iconElement.style.width = `${iconSize}px`;
                    //     iconElement.style.height = `${iconSize}px`;
                    //     iconElement.style.minWidth = `${iconSize}px`;
                    //     iconElement.style.minHeight = `${iconSize}px`;
                    //     iconElement.style.borderWidth = `${Math.max(2, iconSize * 0.04)}px`;
                        
                    //     // Ajustar el contenido interno del icono
                    //     const iconContent = iconElement.querySelector('.swal2-icon-content');
                    //     if (iconContent instanceof HTMLElement) {
                    //         iconContent.style.fontSize = `${iconSize * 0.75}px`;
                    //     }
                        
                    //     // Ajustar líneas del icono de error
                    //     const xMark = iconElement.querySelectorAll('.swal2-x-mark-line-left, .swal2-x-mark-line-right');
                    //     xMark.forEach((line) => {
                    //         if (line instanceof HTMLElement) {
                    //             line.style.width = `${iconSize * 0.375}px`;
                    //             line.style.height = `${Math.max(2, iconSize * 0.06)}px`;
                    //         }
                    //     });
                    // }
                }
            },
            customClass: {
                popup: getToastThemeClass(theme),
                title: 'text-sm font-medium',
                container: '!z-[1400]',
                timerProgressBar: getTimerProgressBarClass(theme)
            },
            heightAuto: false,
            backdrop: false
        });

        Toast.fire({
            icon: icon as SweetAlertIcon,
            title: (titulo ?? mensaje) || undefined
        }).then(() => {
            if (callback) callback(true);
        });

        return;
    }

    // Configuración para modal normal
    const contenedorTiempo = `${tiempo && tiempo > 0 ? `<span id="mensajeTiempoSWL">${mensajeTiempoSWL} <b></b> segundos.</span>` : ''}`;

    /* Insignia circular con ícono personalizado (sustituye el ícono nativo de SweetAlert2) */
    const insigniaIconoPersonalizado = iconoPersonalizado
        ? `<div class="flex flex-col items-center gap-3 pt-1">
            <div class="relative w-24 h-24 flex items-center justify-center">
                <div class="absolute inset-0 rounded-full bg-blue-100/60"></div>
                <span class="absolute w-2 h-2 rounded-full bg-blue-300" style="top:2px;left:14px;"></span>
                <span class="absolute w-1.5 h-1.5 rounded-full bg-blue-300" style="top:18px;right:4px;"></span>
                <span class="absolute w-1.5 h-1.5 rounded-full bg-blue-300" style="bottom:10px;left:2px;"></span>
                <div class="relative w-16 h-16 rounded-full flex items-center justify-center shadow-md" style="background:linear-gradient(135deg,#5B8DEF,#1040CF);">
                    ${iconoPersonalizado}
                </div>
            </div>
            ${titulo ? `<h2 class="text-2xl font-bold text-[#0F172A]">${titulo}</h2>` : ''}
            <hr class="w-full border-t border-gray-200"/>
        </div>`
        : '';

    Swal.fire({
        icon: iconoPersonalizado ? undefined : icon,
        title: iconoPersonalizado ? undefined : (titulo || undefined),
        html: iconoPersonalizado
            ? `${insigniaIconoPersonalizado}${mensaje ? `<div class="pt-3 text-center">${mensaje}${contenedorTiempo}</div>` : ''}`
            : (mensaje
                ? `<div>
                ${mensaje}
                <br/>
                ${contenedorTiempo}
            </div>`
                : ''),
        timer: tiempo && tiempo > 0 ? tiempo * 1000 : undefined,
        timerProgressBar: false,
        showCancelButton: botonCancelar || false,
        confirmButtonText: textoBotonAceptar || 'Aceptar',
        cancelButtonText: textoBotonCancelar || 'Cancelar',
        confirmButtonColor: colorBotonAceptar || '#0050dd',
        cancelButtonColor: colorBotonCancelar || '#ef3340',
        customClass: {
            confirmButton: '!rounded-r-xl !rounded-l-xl !py-2',
            cancelButton: '!rounded-r-xl !rounded-l-xl !py-2',
            popup: getModalThemeClass(theme),
            container: '!z-[1400]'
        },
        heightAuto: false,
        allowOutsideClick: clic,
        allowEscapeKey: esc,
        didOpen: () => {
            if (loading) Swal.showLoading();
            
            // Aplicar tamaño del icono si se especificó
            if (iconSize) {
                // const iconElement = Swal.getIcon();
                // if (iconElement) {
                //     iconElement.style.width = `${iconSize}px`;
                //     iconElement.style.height = `${iconSize}px`;
                //     iconElement.style.minWidth = `${iconSize}px`;
                //     iconElement.style.minHeight = `${iconSize}px`;
                //     iconElement.style.borderWidth = `${Math.max(2, iconSize * 0.04)}px`;
                    
                //     // Ajustar el contenido interno del icono (X, ✓, ?, i, etc.)
                //     const iconContent = iconElement.querySelector('.swal2-icon-content');
                //     if (iconContent instanceof HTMLElement) {
                //         iconContent.style.fontSize = `${iconSize * 0.75}px`;
                //     }
                    
                //     // Ajustar líneas del icono de error
                //     const xMark = iconElement.querySelectorAll('.swal2-x-mark-line-left, .swal2-x-mark-line-right');
                //     xMark.forEach((line) => {
                //         if (line instanceof HTMLElement) {
                //             line.style.width = `${iconSize * 0.375}px`;
                //             line.style.height = `${Math.max(2, iconSize * 0.06)}px`;
                //         }
                //     });
                // }
            }

            if (tiempo && tiempo > 0) {
                const timerInterval = setInterval(() => {
                    const b = Swal.getHtmlContainer()?.querySelector('b');
                    if (b) {
                        const seg = Math.ceil((Swal.getTimerLeft() || 0) / 1000);
                        b.textContent = String(seg);
                    }
                }, 100);
                Swal.getHtmlContainer()?.setAttribute('data-timer-interval', String(timerInterval));
            }
        },
        willClose: () => {
            if (tiempo && tiempo > 0) {
                const timerInterval = Swal.getHtmlContainer()?.getAttribute('data-timer-interval');
                if (timerInterval) {
                    clearInterval(Number(timerInterval));
                    if (callback) callback(true);
                }
            }
        }
    }).then((result) => {
        if (callback) callback(result.isConfirmed);
    });
}

/**
 * Obtiene las clases de tema para toast
 */
function getToastThemeClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-gray-800 !text-white';
        case 'colored':
            return '!bg-gradient-to-r !from-blue-500 !to-blue-600 !text-white';
        case 'light':
        default:
            return '!bg-white !text-gray-800';
    }
}

/**
 * Obtiene las clases de tema para modal
 */
function getModalThemeClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-gray-800 !text-white';
        case 'colored':
            return '!bg-gradient-to-br !from-blue-50 !to-indigo-50';
        case 'light':
        default:
            return '';
    }
}

/**
 * Obtiene las clases para la barra de progreso del timer según el tema
 */
function getTimerProgressBarClass(theme: 'light' | 'dark' | 'colored'): string {
    switch (theme) {
        case 'dark':
            return '!bg-blue-400'; // Color claro para contraste en fondo oscuro
        case 'colored':
            return '!bg-white !opacity-80';
        case 'light':
        default:
            return '!bg-blue-600'; // Color oscuro para contraste en fondo claro
    }
}

export default customAlert;
